// One unread count, shared by every component that shows it.
//
// The Navbar renders a NotificationBell twice — once in the desktop cluster
// (`hidden md:flex`) and once in the mobile cluster (`md:hidden`). Tailwind's
// `hidden` is CSS: both instances still mount, still run their effects, and
// still fetch. So every page load sent two /api/notifications/unread-count
// requests, and every tab-focus sent two more, for a single number that is
// identical in both copies. Half of that endpoint's traffic was the same badge
// asking twice.
//
// This store makes the fetch shared rather than per-instance: concurrent
// callers join the in-flight request, and a caller arriving just after a fresh
// one reuses the value. Adding a third bell somewhere costs zero extra
// requests, which is the property that actually keeps this fixed.

import api from './api';

// A count re-read within this window reuses the last one. Sized to collapse the
// burst cases (two bells mounting, a visibilitychange firing both listeners)
// without holding a stale number long enough for anyone to notice.
const TTL_MS = 10000;

let value = 0;
let fetchedAt = 0;
let inFlight = null;
const listeners = new Set();

const emit = () => {
  listeners.forEach((fn) => {
    try { fn(value); } catch { /* a bad subscriber must not break the others */ }
  });
};

export const getUnreadCount = () => value;

export const subscribeUnreadCount = (fn) => {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
};

// Local corrections (mark-as-read, mark-all-read) — no round trip, and every
// mounted bell moves together instead of one going stale until its next fetch.
export const setUnreadCount = (next) => {
  value = Math.max(0, Number(next) || 0);
  fetchedAt = Date.now();
  emit();
};

// Skipped while offline so a dropped mobile connection doesn't retry, each one
// logging a spurious "server error" for what is really just no signal.
export const refreshUnreadCount = async ({ force = false } = {}) => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return value;
  if (inFlight) return inFlight;
  if (!force && Date.now() - fetchedAt < TTL_MS) return value;

  inFlight = api
    .get('/api/notifications/unread-count')
    .then((res) => {
      setUnreadCount(res?.data?.count);
      return value;
    })
    .catch(() => value) // badge just stays at its last known count
    .finally(() => { inFlight = null; });

  return inFlight;
};

// Test seam: jest modules are cached across tests in a file, so the counters
// above would otherwise leak between cases.
export const __resetUnreadCountStore = () => {
  value = 0;
  fetchedAt = 0;
  inFlight = null;
  listeners.clear();
};
